import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { track } from "@/lib/analytics";
import {
  getStoredAudience,
  setAudiencePreference,
  type TrafficAudience,
} from "@/lib/profile-traffic";

/**
 * Who the public pages are speaking to: "public" (patients and carers — the
 * default) or "clinician" (GP mode: referral details and GP notes up front).
 * An explicit toggle wins; otherwise signed-in GPs/specialists get GP mode.
 * The same value buckets first-party profile traffic.
 */
interface AudienceContextValue {
  audience: TrafficAudience;
  isGpMode: boolean;
  setAudience: (audience: TrafficAudience) => void;
}

const AudienceContext = createContext<AudienceContextValue | null>(null);

export function AudienceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, hasAnyRole } = useAuth();
  const [stored, setStored] = useState<TrafficAudience | null>(() => getStoredAudience());
  const audience: TrafficAudience =
    stored ?? (isAuthenticated && hasAnyRole("gp", "specialist") ? "clinician" : "public");

  const setAudience = useCallback((next: TrafficAudience) => {
    setAudiencePreference(next);
    setStored(next);
    track("audience_toggle", { audience: next });
  }, []);

  const value = useMemo(
    () => ({ audience, isGpMode: audience === "clinician", setAudience }),
    [audience, setAudience],
  );
  return <AudienceContext.Provider value={value}>{children}</AudienceContext.Provider>;
}

export function useAudience(): AudienceContextValue {
  const value = useContext(AudienceContext);
  if (!value) throw new Error("useAudience must be used inside <AudienceProvider>");
  return value;
}
